import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Save } from "lucide-react";

const STATUS = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_LABEL = {
  pending: "Ausstehend",
  confirmed: "Bestätigt",
  completed: "Abgeschlossen",
  cancelled: "Storniert",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen grain">
      <Header />
      <main className="relative max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-2">Admin</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight">
            Glanzwerk <span className="text-gradient-gold">Cockpit</span>
          </h1>
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="bg-zinc-950 border border-white/10 rounded-full p-1">
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings" className="rounded-full">Buchungen</TabsTrigger>
            <TabsTrigger value="customers" data-testid="admin-tab-customers" className="rounded-full">Kunden</TabsTrigger>
            <TabsTrigger value="packages" data-testid="admin-tab-packages" className="rounded-full">Pakete</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings" className="rounded-full">Einstellungen</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-8"><BookingsTab /></TabsContent>
          <TabsContent value="customers" className="mt-8"><CustomersTab /></TabsContent>
          <TabsContent value="packages" className="mt-8"><PackagesTab /></TabsContent>
          <TabsContent value="settings" className="mt-8"><SettingsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/bookings/${id}/status`, { status });
      toast.success("Status aktualisiert");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  if (loading) return <div className="text-zinc-500">Lade…</div>;
  if (bookings.length === 0) return <div className="text-zinc-500">Keine Buchungen.</div>;

  return (
    <div className="grid gap-3">
      {bookings.map((b) => (
        <div
          key={b.booking_id}
          data-testid={`admin-booking-${b.booking_id}`}
          className="rounded-xl border border-white/10 bg-zinc-950 p-5 grid lg:grid-cols-[1fr_auto] gap-4 items-center"
        >
          <div className="grid sm:grid-cols-5 gap-3 text-sm">
            <div className="sm:col-span-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Kunde</div>
              <div className="font-medium mt-1">{b.user_name || b.user_email}</div>
              <div className="text-xs text-zinc-500">{b.user_email}</div>
              {b.guest_phone && <div className="text-xs text-zinc-500">📞 {b.guest_phone}</div>}
              {b.is_guest && <Badge className="mt-1 bg-zinc-800 text-zinc-300 text-[10px]">Gast</Badge>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Paket</div>
              <div className="font-medium mt-1">{b.package_name}</div>
              {b.is_free && <Badge className="mt-1 bg-primary text-primary-foreground text-[10px]">Gratis</Badge>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Termin</div>
              <div className="font-medium mt-1">{b.date} · {b.time}</div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Adresse · Fahrzeug</div>
              <div className="font-medium mt-1 truncate">{b.address}</div>
              <div className="text-xs text-zinc-500 truncate">
                {b.vehicle}
                {b.vehicle_category && ` · ${({ kleinwagen: "Kleinwagen", mittelklasse: "Mittelklasse", suv: "SUV", van: "Van" })[b.vehicle_category] || b.vehicle_category}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={b.status} onValueChange={(v) => setStatus(b.booking_id, v)}>
              <SelectTrigger data-testid={`admin-status-${b.booking_id}`} className="w-44 bg-zinc-900 border-zinc-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-white/10">
                {STATUS.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [stamps, setStamps] = useState(0);
  const [freeCredits, setFreeCredits] = useState(0);

  const load = async () => {
    const { data } = await api.get("/admin/customers");
    setCustomers(data);
  };
  useEffect(() => { load(); }, []);

  const open = (c) => {
    setEditing(c);
    setStamps(c.stamps || 0);
    setFreeCredits(c.free_credits || 0);
  };

  const save = async () => {
    try {
      await api.put(`/admin/customers/${editing.user_id}/stamps`, {
        stamps: parseInt(stamps, 10),
        free_credits: parseInt(freeCredits, 10),
      });
      toast.success("Kunde aktualisiert");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  return (
    <>
      <div className="grid gap-3">
        {customers.map((c) => (
          <div
            key={c.user_id}
            data-testid={`admin-customer-${c.user_id}`}
            className="rounded-xl border border-white/10 bg-zinc-950 p-5 flex flex-wrap items-center gap-4 justify-between"
          >
            <div>
              <div className="font-medium">{c.name || "—"}</div>
              <div className="text-xs text-zinc-500">{c.email}</div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Stempel</div>
                <div className="font-heading text-xl text-primary">{c.stamps || 0}/5</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Gratis</div>
                <div className="font-heading text-xl">{c.free_credits || 0}</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total</div>
                <div className="font-heading text-xl">{c.total_completed || 0}</div>
              </div>
              <Button
                data-testid={`admin-customer-edit-${c.user_id}`}
                size="sm"
                variant="outline"
                onClick={() => open(c)}
                className="rounded-full border-white/15 bg-transparent hover:bg-white/5"
              >
                <Edit className="w-3 h-3 mr-1" /> Bearbeiten
              </Button>
            </div>
          </div>
        ))}
        {customers.length === 0 && <div className="text-zinc-500">Noch keine Kunden.</div>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-zinc-950 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Stempel (0–5)</Label>
              <Input
                data-testid="admin-edit-stamps"
                type="number"
                min={0}
                max={5}
                value={stamps}
                onChange={(e) => setStamps(e.target.value)}
                className="mt-2 bg-zinc-900 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Gratis-Credits</Label>
              <Input
                data-testid="admin-edit-credits"
                type="number"
                min={0}
                value={freeCredits}
                onChange={(e) => setFreeCredits(e.target.value)}
                className="mt-2 bg-zinc-900 border-zinc-800"
              />
            </div>
          </div>
          <Button
            data-testid="admin-edit-save"
            onClick={save}
            className="rounded-full bg-primary text-primary-foreground hover:brightness-110"
          >
            <Save className="w-4 h-4 mr-2" /> Speichern
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PackagesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", price: "", description: "", duration_min: 60 });

  const load = async () => {
    const { data } = await api.get("/packages");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm({ name: "", price: "", description: "", duration_min: 60 });
  };
  const startEdit = (p) => {
    setEditing(p.package_id);
    setForm({ name: p.name, price: p.price, description: p.description, duration_min: p.duration_min });
  };
  const save = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price), duration_min: parseInt(form.duration_min, 10) };
      if (editing === "new") {
        await api.post("/packages", payload);
        toast.success("Paket erstellt");
      } else {
        await api.put(`/packages/${editing}`, payload);
        toast.success("Paket aktualisiert");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Paket löschen?")) return;
    await api.delete(`/packages/${id}`);
    load();
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button
          data-testid="admin-package-new"
          onClick={startNew}
          className="rounded-full bg-primary text-primary-foreground hover:brightness-110"
        >
          <Plus className="w-4 h-4 mr-2" /> Neues Paket
        </Button>
      </div>
      <div className="grid gap-3">
        {items.map((p) => (
          <div
            key={p.package_id}
            data-testid={`admin-package-${p.package_id}`}
            className="rounded-xl border border-white/10 bg-zinc-950 p-5 flex flex-wrap items-center justify-between gap-4"
          >
            <div>
              <div className="font-heading text-lg">{p.name}</div>
              <div className="text-xs text-zinc-500 max-w-xl">{p.description}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-gradient-gold font-heading text-xl">{p.price.toFixed(0)}€</div>
              <Button size="sm" variant="outline" onClick={() => startEdit(p)} className="rounded-full border-white/15 bg-transparent">
                <Edit className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(p.package_id)} className="text-zinc-400 hover:text-red-400">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-zinc-950 border-white/10">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Neues Paket" : "Paket bearbeiten"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Input data-testid="pkg-form-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800" />
            <Input data-testid="pkg-form-price" type="number" step="0.01" placeholder="Preis €" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-zinc-900 border-zinc-800" />
            <Input data-testid="pkg-form-duration" type="number" placeholder="Dauer in Min." value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="bg-zinc-900 border-zinc-800" />
            <Textarea data-testid="pkg-form-desc" rows={4} placeholder="Beschreibung" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800" />
          </div>
          <Button data-testid="pkg-form-save" onClick={save} className="rounded-full bg-primary text-primary-foreground hover:brightness-110">
            Speichern
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SettingsTab() {
  const [form, setForm] = useState({ iban: "", bank_name: "", account_holder: "", bic: "", contact_phone: "", contact_email: "" });
  useEffect(() => {
    api.get("/settings").then((r) => setForm({ ...form, ...r.data })); // eslint-disable-line
  }, []);

  const save = async () => {
    try {
      await api.put("/settings", form);
      toast.success("Einstellungen gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-zinc-950 p-8 max-w-2xl">
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">Bankverbindung (für Überweisungen)</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Kontoinhaber" value={form.account_holder} onChange={(v) => setForm({ ...form, account_holder: v })} testid="settings-holder" />
        <Field label="Bank" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} testid="settings-bank" />
        <Field label="IBAN" value={form.iban} onChange={(v) => setForm({ ...form, iban: v })} testid="settings-iban" full />
        <Field label="BIC" value={form.bic} onChange={(v) => setForm({ ...form, bic: v })} testid="settings-bic" />
      </div>
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mt-8 mb-4">Kontakt</div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Telefon" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="settings-phone" />
        <Field label="E-Mail" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="settings-email" />
      </div>
      <Button
        data-testid="settings-save"
        onClick={save}
        className="mt-8 rounded-full bg-primary text-primary-foreground hover:brightness-110"
      >
        <Save className="w-4 h-4 mr-2" /> Speichern
      </Button>
    </div>
  );
}

function Field({ label, value, onChange, testid, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</Label>
      <Input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 bg-zinc-900 border-zinc-800 h-11"
      />
    </div>
  );
}
